import { formatNumber, formatValueWithUnits } from "../../measurement/UnitFormatter";
import { fetchJson } from "../../net/fetch";
import { base64EncArr, BBOX_SIZE, FRAG_SIZE, serializeBoundingBox, serializeFragment } from "../../wgs/scene/FragmentData";
import * as dte from "../DtEventTypes";
import { base64DecToArr, base64DecodedLen } from "../encoding/base64";
import { blobToJson } from "../encoding/utf8";
import { ELEMENT_CLONE_OVERLAY } from "../facets/FacetVizEffects";
import { IotReadings } from "./IotReading";
import { ColumnFamilies, ElementFlags, ModelIdSize, QC, RowKeySize } from "../schema/dt-schema";
import { DtConstants } from "../schema/DtConstants";
import { StreamUtils } from "./StreamUtils";
import ReadingsByDate, { GRANULARITY } from "./ReadingsByDate";
import { TelemetryView } from "./timeline/TelemetryView";
import StreamEvents from "../worker/streamevents/StreamEvents";

const { StreamsImportExportTableHeader, AttributeType, ChangeTypes, StreamOfflineTimeouts } = DtConstants;

const CONNECTIONS_CATEGORY = DtConstants.RC['IoT Connections'];

// TODO: generalize (beyond position+normal) and move to more appropriate location
function createInterleavedGeom(bufferGeom) {

  const vertices = bufferGeom.attributes.position.array;
  const normals = bufferGeom.attributes.normal.array;
  const indices = bufferGeom.attributes.index.array;

  var geom = new THREE.BufferGeometry();

  var vb = new Float32Array(vertices.length + normals.length);

  geom.vbstride = 6;

  geom.vb = vb;
  geom.ib = indices;

  // fill interleaved vb
  var dst = 0;
  for (var i = 0; i < vertices.length; i += 3) {
    vb[dst++] = vertices[i];
    vb[dst++] = vertices[i + 1];
    vb[dst++] = vertices[i + 2];
    vb[dst++] = normals[i];
    vb[dst++] = normals[i + 1];
    vb[dst++] = normals[i + 2];
  }

  // position attribute
  var attrPos = new THREE.BufferAttribute(undefined, 3);
  attrPos.itemOffset = 0;
  geom.attributes.position = attrPos;

  // normal attribute
  var attrNormal = new THREE.BufferAttribute(undefined, 3);
  attrNormal.itemOffset = 3;
  geom.attributes.normal = attrNormal;

  // index attribute
  var attrIndex = new THREE.BufferAttribute(undefined, 1);
  attrIndex.bytesPerItem = 2;
  geom.attributes.index = attrIndex;

  geom.attributesKeys = Object.keys(geom.attributes);

  return geom;
}

// Ensures that network calls are not duplicated
function deduplicatedFetch(streamIDs, fetchCB, inflightCache) {
  const idsToRequest = streamIDs.filter((id) => !inflightCache.has(id));

  if (idsToRequest.length > 0) {
    // Set batched data retrieval.
    const request = fetchCB(idsToRequest).
    finally(() => idsToRequest.forEach((id) => inflightCache.delete(id)));
    // Update inflight cache
    idsToRequest.forEach((id) => inflightCache.set(id, request));
  }

  return Promise.all(streamIDs.map((id) => inflightCache.get(id)));
}

export class StreamManager {
  constructor(facility) {
    this.facility = facility;
    this.viewer = undefined;
    this.defaultModel = undefined;

    this.inflightCaches = {
      infos: new Map()
    };
    this.streamsCache = new Map();
    this.readingsByDate = new ReadingsByDate(this);
    this.view = new TelemetryView(this);
    this.streamEvents = new StreamEvents(this);
    this.boundOnModelChanged = this.onModelChanged.bind(this);
    this.boundOnModelMetaChanged = this.onModelMetaChanged.bind(this);
    this.boundOnWsReconnect = this.onWsReconnect.bind(this);
    this.iotWorkerCbId = -1;
  }

  get markers() {
    console.log("DEPRECATED use of StreamManager.markers");
    return this.facility.viewer?.dataViz.markers;
  }

  get heatmap() {
    console.log("DEPRECATED use of StreamManager.heatmap");
    return this.facility.viewer?.dataViz.heatmap;
  }

  get dataVizToolbarCtrl() {
    console.log("DEPRECATED use of StreamManager.dataVizToolbarCtrl");
    return this.facility.viewer?.dataViz;
  }

  // elementId of the root document (which grants access to the webhook endpoints)
  getRootElementID() {
    return 'AQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA';
  }

  // Actual overlay and markers initialization only happen when a default model is found
  _initImpl() {
    this.streamsCache = new Map();
    this.view.init(this.viewer, this.facility.eventTarget);
    this.facility.eventTarget.addEventListener(dte.DT_MODEL_CHANGED_EVENT, this.boundOnModelChanged);
    this.facility.eventTarget.addEventListener(dte.DT_FACILITY_CHANGED_EVENT, this.boundOnModelChanged);
    this.facility.eventTarget.addEventListener(dte.DT_MSG_WS_RECONNECTED, this.boundOnWsReconnect);
    this.addIotEventListener();
  }

  onModelMetaChanged({ change }) {
    if (change.type === 'create' && change.model.urn() === this.facility.getDefaultModelId()) {
      this._initImpl();
      this.facility.eventTarget.removeEventListener(dte.DT_MODEL_METADATA_CHANGED_EVENT, this.boundOnModelMetaChanged);
    }
  }

  init(viewer) {
    this.viewer = viewer;

    // only run internal initialization if there's a default model
    const model = this.facility.getDefaultModel();
    if (model) {
      this._initImpl();
    } else {
      // defer actual init until a default model is created
      this.facility.eventTarget.addEventListener(dte.DT_MODEL_METADATA_CHANGED_EVENT, this.boundOnModelMetaChanged);
    }
  }

  dispose() {
    this.view.dispose();
    if (this.viewer) {
      // Clean up if overlay & markers initialization happened
      this.facility.facetsManager.facetsEffects.clearStreamsOverlay();
      this.streamsCache.clear();
      this.facility.eventTarget.removeEventListener(dte.DT_MODEL_CHANGED_EVENT, this.boundOnModelChanged);
      this.facility.eventTarget.removeEventListener(dte.DT_FACILITY_CHANGED_EVENT, this.boundOnModelChanged);
      this.facility.eventTarget.removeEventListener(dte.DT_MSG_WS_RECONNECTED, this.boundOnWsReconnect);
      this.removeIotEventListener();
    } else {
      // Remove listener waiting for default model creation
      this.facility.eventTarget.removeEventListener(dte.DT_MODEL_METADATA_CHANGED_EVENT, this.boundOnModelMetaChanged);
    }
    this.viewer = undefined;
  }

  async _getDefaultModel() {
    try {
      this.defaultModel = this.facility.getDefaultModel();

      if (!this.defaultModel) {
        return null;
      }

      await this.defaultModel.waitForLoad(false, true);
      return this.defaultModel;
    } catch (e) {
      console.warn('Unable to wait for default model', e);
      return null;
    }
  }

  async _getOrCreateDefaultModel() {
    await this._getDefaultModel();

    if (!this.defaultModel) {
      await this.facility.createDefaultModel();
      this.defaultModel = this.facility.getDefaultModel();
    }

    return this.defaultModel;
  }

  onHostHighlightChange({ model, hostId, highlight }) {
    if (highlight) {
      this.facility.facetsManager.facetsEffects.addElementHighlight(model, hostId, ELEMENT_CLONE_OVERLAY);
    } else {
      this.facility.facetsManager.facetsEffects.removeElementHighlight(model, hostId, ELEMENT_CLONE_OVERLAY);
    }
  }

  async makeStreamGeometry() {
    if (!this.streamGeometryPromise) {
      this.streamGeometryPromise = (async () => {
        const streamGeom = createInterleavedGeom(new THREE.SphereBufferGeometry(0.5, 32, 24));
        const res = await this.defaultModel.createGeometry(streamGeom);
        return res;
      })();
    }
    return this.streamGeometryPromise;
  }

  async addStreamFragmentFromParent(element, model, dbId) {
    const box = model.getElementBounds(dbId);
    if (box.isEmpty()) {
      console.error('Failed to obtain bounding box of parent geometry');
      return;
    }
    const center = box.getCenter();
    center.add(model.getGlobalOffset());


    const { hash } = await this.makeStreamGeometry();
    // TODO: here we are ignoring the transform, as the geometry is the unit sphere.
    let m = new THREE.Matrix4();
    m.setPosition(center);
    const fragmentData = serializeFragment(0, hash, null, m.toArray());
    const lmvFragment = base64EncArr(fragmentData, 0, FRAG_SIZE);

    const bounds = new THREE.Box3();
    bounds.setFromCenterAndSize(center, new THREE.Vector3(1, 1, 1));
    const boundingBoxData = serializeBoundingBox(bounds);
    const lmvBBox = base64EncArr(boundingBoxData, 0, BBOX_SIZE);

    element[DtConstants.QC.LmvFragment] = lmvFragment;
    element[DtConstants.QC.LmvBoundingBox] = lmvBBox;
  }

  // create a logical stream element and link it to some physical asset - link information has to be provided all the time as we do not support creation free floating logical stream elements.
  async createStream(name, modelUrnOfParent, dbIdOfParent, extraProps = {}, wantGeometry = false) {
    const modelOfParent = this.facility.getModelByUrn(modelUrnOfParent);

    const props = {
      [QC.Name]: name,
      [QC.CategoryId]: CONNECTIONS_CATEGORY,
      [QC.ElementFlags]: ElementFlags.Stream,
      ...extraProps
    };
    if (this.facility.getSchemaVersion() > 1) {
      props[QC.TandemCategory] = DtConstants.TC.Sensors;
    } else {
      props[QC.UniformatClass] = "D7070";
    }

    if (!this.defaultModel) {
      await this._getOrCreateDefaultModel();
    }

    if (modelOfParent && dbIdOfParent) {
      await this.addParentXRef([props], [[modelUrnOfParent, dbIdOfParent]]);

      if (wantGeometry) {
        await this.addStreamFragmentFromParent(props, modelOfParent, dbIdOfParent);
      }
    }

    const [newElmKey] = await this.defaultModel.createElementsWithData([props], 'Create streams');

    // set stream ingestion password
    await this.resetStreamSecrets([newElmKey]);
    return newElmKey;
  }

  //  defines which columns will be available in the template - currently we base it of our default parameter set applied to every created stream + standard override property for name
  getStreamsBulkImportTemplate() {
    const template = { ...StreamsImportExportTableHeader };

    return [
    template,
    { "Name": "Stream 1", "Assembly Code": "D7070", "Classification": "", "HostModelID": "", "HostElementID": "" },
    { "Name": "Stream 2", "Assembly Code": "D7070", "Classification": "", "HostModelID": "", "HostElementID": "" }];

  }

  /**
   * Import streams from JSON data.
   * Supports updates to streams by providing the fully-qualified element ID and facility ID. Ignores unchanged streams.
   * Ignores empty values in the CSV, unsetting values is not supported
   * @param {Object} schema
   * @param {Object[]} data
   * @returns {string[]} created/modified stream ids
   */
  async importStreamsFromJsonTemplate(schema, data) {
    const streams = await this.getAllStreamInfos();
    const existingStreams = new Map(streams.map((s) => [s.fullId, s]));

    const defaultModel = await this._getOrCreateDefaultModel();
    const dbId2catId = defaultModel.getData().dbId2catId;

    const parents = {};


    for (let props of data) {
      if (!props.fullId) {
        continue;
      }
      const stream = existingStreams.get(props.fullId);

      if (!stream?.hostElement) {
        continue;
      }

      const [elementId] = await stream.hostElement.model.getElementIdsFromDbIds([stream.hostElement.hostId]);
      parents[stream.fullId] = { modelId: stream.hostElement.model.urn(), elementId };
    }

    const updatedStreams = [];
    const newStreams = [];

    for (let streamProps of data) {
      const res = {
        [QC.ElementFlags]: ElementFlags.Stream
      };

      const existingStream = existingStreams.get(streamProps?.fullId);

      // Compare with existing streams in the facility and filter out incorrect and unchanged
      if (streamProps?.fullId?.length) {
        if (!existingStream) {
          console.error(`Unknkown full id for connection "${streamProps.Name}", skipping...`);
          continue;
        }

        // Check that it is coming from the right facility
        if (!streamProps.facility?.length) {
          console.error(`Facility ID is missing for "${streamProps.Name}", skipping...`);
          continue;
        }

        if (streamProps.facility !== this.facility.urn()) {
          console.error(`Connection "${streamProps.Name}" seems to be coming from another facility. ${streamProps.facility} instead of ${this.facility.urn()}, skipping...`);
          continue;
        }

        // Skip streams where nothing changed
        const noNameChanges = !streamProps.Name || streamProps.Name === existingStream.name;
        const noClassificationChanges = !streamProps.Classification || streamProps.Classification === existingStream.userClassification;
        const noAssemblyCodeChanges = !streamProps["Assembly Code"] || streamProps["Assembly Code"] === existingStream.dtClass;
        const noHostModelChange = !streamProps.HostModelID || streamProps.HostModelID === existingStream.hostElement?.model.urn();
        const noHostElementChange = !streamProps.HostElementID || streamProps.HostElementID === parents[streamProps.fullId]?.elementId;

        if (noNameChanges && noClassificationChanges && noAssemblyCodeChanges && noHostModelChange && noHostElementChange) {
          continue;
        }

        // Fill in missing categoryId for older streams
        if (dbId2catId[existingStream.dbId] !== CONNECTIONS_CATEGORY) {
          res[QC.CategoryId] = CONNECTIONS_CATEGORY;
        }
        res[QC.LmvDbId] = existingStream.dbId;
        updatedStreams.push(res);
      } else {
        // Add default values for new connections
        res[QC.CategoryId] = CONNECTIONS_CATEGORY;
        if (this.facility.getSchemaVersion() > 1) {
          res[QC.TandemCategory] = DtConstants.TC.Sensors;
        } else {
          res[QC.UniformatClass] = "D7070";
        }
        newStreams.push(res);
      }

      const updateLink = {};
      for (let prop in StreamsImportExportTableHeader) {
        if (!streamProps[prop]) {
          // property is not set for the stream
          continue;
        }

        switch (schema[prop]) {
          case StreamsImportExportTableHeader.Name:
            if (!existingStream) {
              res[QC.Name] = streamProps[prop];
            } else if (existingStream.name !== streamProps[prop]) {
              res[QC.OName] = streamProps[prop];
            }
            continue;

          case StreamsImportExportTableHeader["Assembly Code"]:
            if (!existingStream) {
              res[QC.UniformatClass] = streamProps[prop];
            } else if (existingStream.dtClass !== streamProps[prop]) {
              res[QC.OUniformatClass] = streamProps[prop];
            }
            continue;

          case StreamsImportExportTableHeader.Classification:
            if (!existingStream) {
              res[QC.Classification] = streamProps[prop];
            } else if (existingStream.userClassification !== streamProps[prop]) {
              res[QC.OClassification] = streamProps[prop];
            }
            continue;
          case StreamsImportExportTableHeader.HostElementID:
          case StreamsImportExportTableHeader.HostModelID:
            if (streamProps[prop]) {
              updateLink[prop] = streamProps[prop];
            }
            continue;
          default:
            res[ColumnFamilies.DtProperties + ":" + schema[prop]] = streamProps[prop];
        }
      }

      if (updateLink.HostElementID && updateLink.HostModelID) {
        if (
        parents[res.k]?.modelId !== updateLink.HostModelID ||
        parents[res.k]?.elementId !== updateLink.HostElementID)
        {
          // we know the update contains a new link, can convert it to element properties now
          const targetModel = this.facility.getModelByUrn(updateLink.HostModelID);
          if (!targetModel) {
            console.error(`Failed to find target model ${modelId}, skipping the link creation`);
            props.push(res);
            continue;
          }
          const [targetDbId] = await targetModel.getDbIdsFromElementIds([updateLink.HostElementID]);

          if (!targetDbId) {
            if (!targetModel) {
              console.error(`Failed to find target dbId for ${elementId}, skipping the link creation`);
              props.push(res);
              continue;
            }
          }
          await this.addParentXRef([res], [[updateLink.HostModelID, targetDbId]]);
        }
      }
    }

    // No changes detected
    if (!updatedStreams.length && !newStreams.length) {
      console.log('Import finished without changes to connections');
      return [];
    }

    if (!this.defaultModel) {
      await this._getOrCreateDefaultModel();
    }

    const [newKeys, _] = await Promise.all([
    newStreams.length ? this.defaultModel.createElementsWithData(newStreams, 'Create streams') : [],
    updatedStreams.length ? this.defaultModel.updateElementsWithData(updatedStreams, 'Update streams') : []]
    );

    if (newKeys.length) {
      // for new streams set streams secrets
      await this.resetStreamSecrets(newKeys);
    }

    console.log(`Connections import result: ${newKeys.length} new, ${updatedStreams.length} modified, ${data.length - updatedStreams.length - newKeys.length} skipped connections.`);

    return newKeys;
  }

  /**
   * Creates table-like JSON object from given streams
   * @param {Stream[]} [streams] if provided will export only given streams
   * @returns {Promise<*[]>}
   */
  async exportStreamsToJson(streams = []) {
    const defaultModel = this.facility.getDefaultModel();

    if (!streams?.length) {
      console.warn('streams are missing');
      return [StreamsImportExportTableHeader];
    }

    const keys = streams.map((s) => s.fullId);

    const secrets = await fetchJson(this.facility.loadContext, `/models/${defaultModel.urn()}/getstreamssecrets`, "POST", { keys, includeEmpty: true });

    const res = await Promise.all(streams.map(async (s) => {
      const { name, fullId, streamUrlRaw, dtClass, userClassification } = s;
      const ingestionSecret = secrets[fullId];
      if (!ingestionSecret) {
        console.error(`secret is missing for stream ${fullId}`);
      }

      const ingestionUrl = this._makeIngestionUrl(streamUrlRaw, ingestionSecret || 'MISSING_SECRET');
      let elmId;

      if (s.hostElement) {
        [elmId] = await s?.hostElement.model.getElementIdsFromDbIds([s.hostElement.hostId]);
      }
      //  this is the list of data that is actually exported
      return {
        Name: name,
        "Assembly Code": dtClass,
        Classification: userClassification,
        facility: this.facility.urn(),
        fullId,
        ingestionUrl,
        HostElementID: elmId,
        HostModelID: s.hostElement?.model.urn()
      };

    }));

    return [StreamsImportExportTableHeader, ...res];
  }

  async updateLink(streams, hosts) {
    if (streams.length !== hosts.length) {
      console.error("Streams/Targets length mismatch");
      return;
    }

    if (streams.length < 1) {
      return; // no change
    }

    const changeData = streams.map((stream) => ({
      [QC.LmvDbId]: stream.dbId,
      [QC.XParent]: ""
    }));

    await this.addParentXRef(changeData, hosts);

    const defaultModel = this.facility.getDefaultModel();
    return await defaultModel.updateElementsWithData(changeData, "Update streams relationship");
  }

  validateThresholds(thresholds) {
    if (!thresholds) return true; // effectively removes thresholds
    const { lower, upper } = thresholds;
    const numbers = [lower?.alert, lower?.warn, upper?.warn, upper?.alert].filter(isFinite);

    if (numbers.length === 1) return true; // single threshold is always valid

    for (let n = 1; n < numbers.length; n++) {
      // Must be an array of growing numbers
      if (numbers[n - 1] >= numbers[n]) return false;
    }

    return true;
  }

  /**
   * Apply and remove thresholds for the given streams and parameter
   * @param {Streams[]} streams
   * @param {string} attrID
   * @param {{ name: string, lower: { alert: string, warn: string }, upper: { alert: string, warn: string }}} [newThresholds] omit for removing thresholds
   */
  async updateBulkThresholds(streams, attrID, newThresholds) {
    if (!streams?.length) {
      return; // no change
    }

    if (!this.validateThresholds(newThresholds)) {
      throw new Error(`Invalid threshold value(s) for attribute: ${attrID}`);
    }

    if (!streams.every((stream) => stream.streamAttrs.some((attr) => attr.id === attrID))) {
      throw new Error(`Attribute ${attrID} is not applied to some of the streams`);
    }

    const changeData = [];

    for (const stream of streams) {
      // Rebuild old thresholds because we want to keep streamAttrs.allowedValues.thresholds schema
      const thresholdsByAttrID = stream.streamAttrs.reduce((th, attr) => {
        th[attr.id] = attr.allowedValues?.thresholds;
        return th;
      }, {});

      thresholdsByAttrID[attrID] = newThresholds;

      let storedThresholds;
      for (const aID in thresholdsByAttrID) {
        // Skip undefined thresholds when building BLOB
        if (thresholdsByAttrID[aID]) {
          storedThresholds ??= {};
          storedThresholds[aID] = thresholdsByAttrID[aID];
        }
      }

      const settings = structuredClone(stream.settings || {});

      if (storedThresholds) {
        settings.thresholds = storedThresholds;
      } else {
        delete settings.thresholds;
      }

      const change = {
        [QC.LmvDbId]: stream.dbId,
        [QC.Settings]: Object.keys(settings).length > 0 ? settings : null
      };

      changeData.push(change);
    }

    const defaultModel = this.facility.getDefaultModel();
    await defaultModel.updateElementsWithData(changeData, "Update streams thresholds");

    // Update cached value
    const updatedStreams = [];

    for (const stream of streams) {
      const attr = stream.streamAttrs.find((attr) => attr.id === attrID);
      attr.allowedValues ??= {};
      attr.allowedValues.thresholds = newThresholds;
      updatedStreams.push(stream.dbId);
    }

    this.facility.eventTarget.dispatchEvent({ type: dte.DT_STREAMS_INFO_CHANGED_EVENT, dbIds: updatedStreams });
  }

  /**
   * @returns {Promise<{ name: string, lower: { alert: string, warn: string }, upper: { alert: string, warn: string }, streams: Stream[], attribute: Attr}[]>} - Compiled list of thresholds, augmented with streams and the attributes they target
   */
  async getThresholds() {
    const thresholds = new Map();

    const streams = await this.getAllStreamInfos();

    for (let stream of streams) {
      for (let attr of stream.streamAttrs) {
        if (attr.allowedValues?.thresholds) {
          const threshold = attr.allowedValues.thresholds;

          if (thresholds.has(threshold.name)) {

            if (thresholds.get(threshold.name).attribute.id === attr.id) {
              thresholds.get(threshold.name).streams.push(stream);
            } else {
              console.warn(`Wrong threshold-attribute assignment. Threshold: ${threshold.name} / Stream ID: ${stream.fullId} / Attribute: ${attr.id}`);
            }

            continue;
          }

          thresholds.set(threshold.name, { ...threshold, attribute: attr, streams: [stream] });
        }
      }
    }

    return Array.from(thresholds.values());
  }

  /**
   * Data Points for the given stream, can be from cache.
   *
   * As this function is often called simultaneously on an empty cache, it takes extra-precaution to ensure network
   * requests are not unnecessarily duplicated.
   * @param {number[]} dbIds Stream IDs
   * @param {boolean} wantAlerts if threshold states should be computed
   * @returns {Promise<*[]>}
   */
  async getLastReadings(dbIds = [], wantAlerts = false) {
    if (dbIds.length === 0) {
      return [];
    }

    const lastByDbId = await this.getLastReadingsFromWorker({ dbIds });
    // Ensures stream cache is populated.
    if (wantAlerts) await this.getAllStreamInfos(false, dbIds);

    const formattedResults = [];
    for (let streamIdx = 0; streamIdx < dbIds.length; streamIdx++) {
      const dbId = dbIds[streamIdx];
      const readingsByAttrID = lastByDbId.get(dbId);
      if (wantAlerts && !readingsByAttrID) {
        formattedResults[streamIdx] = { state: DtConstants.StreamStates.NoData };
        continue;
      }
      // Build allowedValuesByAttrID because we want to keep streamAttrs.allowedValues.thresholds schema
      let stream;
      let thresholdsByAttrID;
      if (wantAlerts) {
        stream = this.streamsCache.get(dbId);
        thresholdsByAttrID = stream?.streamAttrs.reduce((th, attr) => {
          th[attr.id] = attr.allowedValues?.thresholds;
          return th;
        }, {}) ?? {};
      }

      // make a copy here to avoid modifying the cache entry.
      const enrichedData = {};
      for (let attrID in readingsByAttrID) {
        const reading = readingsByAttrID[attrID];
        let val, ts;
        for (const time in reading) {
          val = reading[time];
          ts = time;
          break;
        }
        const newEntry = { ts: Number(ts), val };

        // If the cached stream entry is missing (due to some race), we still provide the consumer with base data.
        if (wantAlerts && stream) {
          newEntry.state = StreamUtils.evaluateState(thresholdsByAttrID[attrID], val, ts, stream.settings.offlineTimeout);
        }

        enrichedData[attrID] = newEntry;
      }
      formattedResults[streamIdx] = enrichedData;
    }

    return formattedResults;
  }

  /** Time of the latest reading. */
  async getLatestReading() {
    // Waits for the instance tree to be loaded, since this is called early.
    const model = await this._getDefaultModel();
    return model?.asyncPropertyOperation({
      "operation": 'GET_LAST_READINGS',
      filter: { onlyLatest: true }
    });
  }

  async getLastReadingsFromWorker(filter, forceRefresh) {
    const model = this.facility.getDefaultModel();
    return model.asyncPropertyOperation({
      "operation": 'GET_LAST_READINGS',
      filter,
      ...(forceRefresh && { forceRefresh })
    });
  }

  async refreshStreamsLastReadings() {
    const model = this.facility.getDefaultModel();
    return model.asyncPropertyOperation({ "operation": 'GET_LAST_READINGS', forceRefresh: true });
  }

  _makeIngestionUrl(rawStreamUrl, secret) {
    const url = new URL(this.facility.loadContext.endpoint);
    url.password = secret;
    url.pathname += rawStreamUrl;

    return url.href;
  }

  // returns an ingestion url for the given stream that allows user to push data without supplying forge JWT token
  async getStreamSecret(key) {
    const defaultModel = await this._getOrCreateDefaultModel();
    const res = await fetchJson(this.facility.loadContext, `/models/${defaultModel.urn()}/getstreamssecrets`, "POST", { keys: [key] });

    return res[key];
  }

  // unless hardReset is true, the reset will not replace existing secrets (so it can be used only to backfill empty ones)
  async resetStreamSecrets(keys, hardReset = false) {
    const defaultModel = this.facility.getDefaultModel();
    return fetchJson(this.facility.loadContext, `/models/${defaultModel.urn()}/resetstreamssecrets`, "POST", {
      keys,
      hardReset
    });
  }

  // returns an ingestion url for the given stream that allows user to push data without supplying forge JWT token
  async getStreamIngestionUrl(stream) {
    const secret = await this.getStreamSecret(stream.fullId);
    return this._makeIngestionUrl(stream.streamUrlRaw, secret);
  }

  getWebhookUrl(type = "generic") {
    const defaultModel = this.facility.getDefaultModel();
    return this._makeIngestionUrl(`/timeseries/models/${defaultModel.urn()}/webhooks/${type}`, "");
  }

  formatStreamValue(value, attribute) {
    // precision of 0 means default of 2 digits for connected attributes.
    const precision = attribute.precision || 2;
    if (attribute.forgeUnit) {
      return formatValueWithUnits(
        value,
        "autodesk.unit.unit:" + attribute.forgeUnit,
        3,
        precision,
        { symbolKey: attribute.forgeSymbol }
      );
    }
    if (attribute.dataType === AttributeType.String) {
      return attribute.allowedValues.list[value];
    }
    // Also format the value as a string to ensure that the precision property is respected.
    return formatNumber(value, precision);
  }

  /**
   * Find attributes that can be used as "connected" for the given stream
   * @param {Object} stream stream object
   * @param {Object[]} [attrs] default model attrs, more efficient if possible in given context
   */
  async getAttrCandidates(stream, attrs) {
    const defaultModel = this.facility.getDefaultModel();
    if (!attrs) {
      attrs = await defaultModel.getAttributes({ native: true });
    }
    const props = await defaultModel.getPropertiesDt([stream.dbId]);
    const elementPropIds = new Set(Object.values(props[0].element.properties).map(({ uuid }) => uuid));
    const attrCandidates = attrs.filter((attr) => {
      if (!elementPropIds.has(attr.uuid)) {
        return false;
      }
      if (attr.dataType == AttributeType.Integer ||
      attr.dataType == AttributeType.Float ||
      attr.dataType == AttributeType.Double)
      {
        return true;
      } else if (attr.dataType == AttributeType.String) {
        return attr.allowedValues?.list?.length > 0;
      }
      return false;
    });

    return attrCandidates;
  }

  async getAllConnectedAttributes() {
    const defaultModel = this.facility.getDefaultModel();
    if (!defaultModel) {
      return [];
    }

    await defaultModel.waitForLoad(false, true);

    const attrs = await defaultModel.getAttributes({ native: true });
    return attrs.filter((attr) => (attr.flags & DtConstants.AttributeFlags.afStream) !== 0);
  }

  async getAllStreamInfos(clearCache = false, dbIds) {
    const streamIds = dbIds ?? (await this.getStreamIds());
    const hadCache = !!this.streamsCache.size;

    if (clearCache) {
      this.streamsCache.clear();
      this.inflightCaches.infos.clear();
    }

    // All streams were removed
    if (streamIds.length === 0 && clearCache && hadCache) {
      this.facility.eventTarget.dispatchEvent({ type: dte.DT_STREAMS_INFO_CHANGED_EVENT, dbIds: [] });
      return [];
    }

    const missingFromCache = streamIds.filter((streamId) => !this.streamsCache.has(streamId));

    if (missingFromCache.length > 0) {
      await deduplicatedFetch(missingFromCache, this._fetchAllStreamInfoAndUpdateCache.bind(this), this.inflightCaches.infos);
    }

    return streamIds.
    map((streamId) => this.streamsCache.get(streamId))
    // Filter out undefined. Can happen when streamIds and cache are not in sync anymore due to other parallel events
    .filter(Boolean);
  }

  async _fetchAllStreamInfoAndUpdateCache(streamIDs = []) {
    const defaultModel = this.facility.getDefaultModel();

    if (!defaultModel) {
      return [];
    }

    const dtPkg = defaultModel.getData();
    const parentXrefs = streamIDs.map((streamId) => dtPkg.xrefId2fullId[dtPkg.dbId2xparentId[streamId]]);

    const [fullIds, resolvedParentXRefs, props] = await Promise.all([
    defaultModel.getElementIdsFromDbIds(streamIDs),
    this.facility.resolveXrefs(parentXrefs),
    defaultModel.query({
      dbIds: streamIDs,
      includes: { standard: true, applied: true, element: true, type: false }
    })]
    );

    let updatedStreams = [];
    for (let idx = 0; idx < streamIDs.length; idx++) {
      const dbId = streamIDs[idx];
      const fullId = fullIds[idx];

      const row = props.rows[idx];

      const name = row[QC.Name];
      const dtClass = row[QC.OUniformatClass] || row[QC.UniformatClass];
      const userClassification = row[QC.OClassification] || row[QC.Classification];

      // to fetch raw data of the stream
      const streamUrlRaw = `/timeseries/models/${defaultModel.urn()}/streams/${fullId}`;

      const candidates = await this.getAttrCandidates({ dbId }, props.cols);
      const streamAttrs = candidates.
      filter((attr) => (attr.flags & DtConstants.AttributeFlags.afStream) !== 0).
      map((attr) => {
        const _attr = structuredClone(attr);
        if (_attr.precision === 0) {
          // backend will fall back to "2" when precision is not set explicitly
          // so we do the same here to make sure properties values formatting works as expected
          _attr.precision = 2;
        }

        // Remove legacy thresholds
        _attr.allowedValues ??= {};
        _attr.allowedValues.thresholds = null;
        return _attr;
      });

      const parent = resolvedParentXRefs[idx];
      if (parentXrefs[idx] && !parent) {
        console.warn(`Failed to resolve parent xref for stream "${name}" (${fullId})`);
      }

      // Use per-element thresholds
      let settings = row[QC.Settings];

      if (settings?.thresholds) {
        // Overwrite all attribute thresholds if the thresholds exist in the settings row
        for (const attr of streamAttrs) {
          if (!attr) continue;
          attr.allowedValues.thresholds = settings.thresholds[attr.id]?.name ? settings.thresholds[attr.id] : null;
        }
      }

      // Ensure offlineTimeout is set for all streams
      if (settings?.offlineTimeout === undefined) {
        if (!settings) {
          settings = {};
        }

        settings.offlineTimeout = StreamOfflineTimeouts.Default;
      }

      const info = {
        dbId,
        name,
        dtClass,
        userClassification,
        streamUrlRaw,
        fullStreamUrlRaw: this.facility.loadContext.endpoint + streamUrlRaw,
        fullId,
        hostElement: parent ? { model: parent.model, hostId: parent.dbId } : undefined,
        streamAttrs,
        settings
      };

      if (!this.streamsCache.has(dbId)) {
        this.streamsCache.set(dbId, info);
        updatedStreams.push(dbId);
      }
    }

    if (updatedStreams.length > 0)
    this.facility.eventTarget.dispatchEvent({ type: dte.DT_STREAMS_INFO_CHANGED_EVENT, dbIds: updatedStreams });
  }

  getAllStreamInfosFromCache() {
    return [...this.streamsCache.values()];
  }

  addIotEventListener() {
    const model = this.facility.getDefaultModel();
    if (!model) return;

    const worker = this.facility.getWorker(model.id);
    this.iotWorkerCbId = worker.registerWorkerCallback(
      (details) => this.onIotNotification(details),
      (details) => console.error('TsWorker failure while processing IOT event', details),
      () => console.warn('unimplemented cb being called')
    );
  }

  removeIotEventListener() {
    const model = this.facility.getDefaultModel();
    if (!model || this.iotWorkerCbId === -1) return;

    const worker = this.facility.getWorker(model.id);
    worker.unregisterWorkerCallback(this.iotWorkerCbId);
  }

  /**
   * Receives IOT events (other than DT_STREAMS_LAST_READINGS_CHANGED_EVENT) from the TsWorker.
   * @param {string} type
   * @param {any} change
   */
  onIotNotification({ type, change }) {
    switch (type) {
      case dte.DT_TELEMETRY_INSTANT_READINGS_CHANGED_EVENT:
        return this.view.handleInstantReadingsChanged(change);
      case dte.DT_TELEMETRY_REGIONS_EVENT:
        return this.view.handleOocRegionsChanged(change);
      // future: we can have an event about arbitrary data changes for "bulk data" and the likes
      default:
        console.info('received unknown IOT event type', type);
        return;
    }
  }

  onModelChanged(e) {
    // Ignore stream data changes, as it doesn't affect the stream info
    if (e.change.ctype === ChangeTypes.DeleteStreamData) {
      return;
    }

    // Ignore events for some model which will be reloaded
    if (e.change.needModelContentsReload) {
      return;
    }

    this.getAllStreamInfos(true);
  }

  onWsReconnect() {
    // As soon as we disconnected, we could have been missing IOT update events. This will force the next access
    // to re-request the latest readings. We don't have a fallback to REST API while WS is down,
    // since it would add complexity and probably also be down.
    this.defaultModel?.asyncPropertyOperation({ operation: "MARK_IOT_STALE" });
  }

  // replicates the room references from a given element into elmData
  async addRoomFromElement(elmData, model, dbId) {
    const pkg = model.getData();
    const roomIds = pkg.dbId2roomIds[dbId];
    const xroomIds = pkg.dbId2xroomIds[dbId];

    let xrefs = [];
    if (Array.isArray(xroomIds)) {
      xrefs = xroomIds.map((xrefId) => pkg.xrefId2fullId[xrefId]);
    } else if (xroomIds) {
      xrefs.push(pkg.xrefId2fullId[xroomIds]);
    }

    if (roomIds) {
      const refIds = await model.getElementIdsFromDbIds(Array.isArray(roomIds) ? roomIds : [roomIds]);
      refIds.forEach((id) => xrefs.push([model.urn(), id]));
    }

    if (!xrefs.length && model.isRoom(dbId)) {
      const refIds = await model.getElementIdsFromDbIds([dbId]);
      refIds.forEach((id) => xrefs.push([model.urn(), id]));
    }

    if (!xrefs.length) {
      return;
    }
    let buf = new Uint8Array(xrefs.length * RowKeySize);

    xrefs.forEach(([urn, id], idx) => {
      const modelGuid = urn.slice(DtConstants.DT_MODEL_URN_PREFIX.length + 1);
      base64DecToArr(modelGuid, buf, idx * RowKeySize);

      base64DecToArr(id, buf, idx * RowKeySize + ModelIdSize);
    });

    elmData[QC.XRooms] = base64EncArr(buf);
  }

  async addParentXRef(elements, linksTo) {
    //Convert link dbIds to database row IDs
    let allLinks = linksTo.reduce((acc, linkTo) => {
      if (linkTo) {
        let list = acc[linkTo[0]];
        if (!list) {
          acc[linkTo[0]] = list = [];
        }
        list.push(linkTo[1]);
      }
      return acc;
    }, {});

    let encodedIds = await this.facility.encodeIds(allLinks, true);

    for (let idx = 0; idx < elements.length; idx++) {
      const elmData = elements[idx];
      const linkTo = linksTo[idx];

      if (linkTo) {
        const [urn, dbId] = linkTo;
        const model = this.facility.getModelByUrn(urn);
        // XParent
        elmData[QC.XParent] = encodedIds[urn][dbId];

        // XRooms
        // here we only copy room references if the destination element has none of its own yet
        // TODO: let the caller and ultimately the user decide
        const elementId = elmData[QC.LmvDbId];
        const existingRooms = this.defaultModel.getData().dbId2xroomIds[elementId];
        const wantRoom = elementId === undefined || !existingRooms || existingRooms.length === 0;
        if (wantRoom) {
          await this.addRoomFromElement(elmData, model, dbId);
        }

        // Level
        const wantLevel = elementId === undefined || !this.defaultModel.getData().dbId2levelId[elementId];
        if (wantLevel) {
          const levelId = model.getData().dbId2levelId[dbId];
          if (levelId > 0) {
            await this.facility.addLevelFromElement(elmData, model, levelId);
          }
        }
      }
    }
  }

  /**
   * Deletes streams incl. timeseries data
   * @param {string[]} streamDbIds
   * @param {number} chunkSize batch size for deleting streams, avoids overloading the server in a single call
   */
  async deleteStreams(streamDbIds, chunkSize = 50) {
    if (!streamDbIds?.length) {
      console.warn('stream ids are missing');
      return;
    }

    let defaultModel = this.facility.getDefaultModel();

    // delete stream elements
    await defaultModel.deleteElements(streamDbIds, 'stream-delete');
  }

  async getStreamIds() {
    const defaultModel = await this._getDefaultModel();

    if (!defaultModel) {
      return [];
    }

    let streamIds = [];
    const dbId2flags = defaultModel.getData().dbId2flags;
    for (let i = 0; i < dbId2flags.length; ++i) {
      if (dbId2flags[i] === ElementFlags.Stream) {
        streamIds.push(i);
      }
    }

    return streamIds;
  }

  // Goal is:
  //   - Optionally take stream, streams, or nothing (all).
  //   - Remove need to pre-ask for formatting or other processing, let them do it with the returned object.
  //
  // Overall design
  //   Automatically get instant readings + last readings, in the same object.
  //   Offer a client side hook able to keep this object updated dynamically (react style).

  /**
   * IOT readings for an instant in time.
   * @param {number[]} [dbIds] Optional list of dbIds to restrict the data to.
   * @return {Promise<IotReadings>}
   */
  async getReadings(dbIds) {
    const last = await this.getLastReadingsFromWorker({ dbIds });
    const instant = await this.view.getInstantReadings({ dbIds });

    return new IotReadings(instant, last, this, dbIds);
  }

  /**
   * Store path and timestamp mapping for stream readings
   * Overrules mappings stored at the parameter level in facility template
   * "path" and "ts" are specified in JSONPath without the leading '$.'.
   *
   * @param {number} dbId
   * @param {undefined | {[attrID: string]: {path: string, ts: string} }} [mapping] overwrites previous mappings. Example: { "z:CQ": { path: "sensor.data.val", ts: "sensor.ts" } }
   */
  async setSourceMappingOverwrite(dbId, mapping) {
    const streams = await this.getAllStreamInfos();
    const stream = streams.find((s) => s.dbId === dbId);

    if (!stream) {
      throw new Error(`Failed to find stream: ${id}`);
    }

    const attrs = await this.getAttrCandidates(stream);

    Object.entries(mapping ?? {}).forEach(([attrId, val]) => {
      if (!attrs.some((a) => a.id === attrId)) {
        throw new Error(`Attribute ${attrId} is not applied to the stream`);
      }
      if (val?.path && typeof val.path !== 'string') {
        throw new Error(`Attribute "${attrId}" has an invalid value path`);
      }
      if (val?.ts && typeof val.ts !== 'string') {
        throw new Error(`Attribute "${attrId}" has an invalid timestamp path`);
      }
    });

    const settings = stream.settings ?? {};
    let changes;

    if ((!mapping || !Object.keys(mapping).length) && settings.sourceMapping) {
      // Clear existing mappings
      const { sourceMapping, ...other } = settings;
      changes = [{
        [QC.LmvDbId]: dbId,
        [QC.Settings]: other
      }];
    } else if (Object.keys(mapping).length) {
      // Overwrite existing mappings
      const newMappings = {};
      attrs.forEach((attr) => {
        if (mapping[attr.id]) {
          newMappings[attr.id] = {
            path: mapping[attr.id].path,
            ts: mapping[attr.id].ts
          };
        }
      });
      changes = [{
        [QC.LmvDbId]: dbId,
        [QC.Settings]: { ...settings, sourceMapping: newMappings }
      }];
    }

    if (!changes) {
      return;
    }

    const defaultModel = this.facility.getDefaultModel();
    await defaultModel.updateElementsWithData(changes, 'Update stream readings mapping');

    this.facility.eventTarget.dispatchEvent({ type: dte.DT_STREAMS_INFO_CHANGED_EVENT, dbIds: changes.map((c) => c[QC.LmvDbId]) });
  }

  /**
   * Set the time period after which connections are considered offline. In seconds, 0 = never.
   * @param {[number, number][]} timeouts - array of [dbId, timeoutInSeconds] pairs
   */
  async setOfflineTimeout(timeouts) {
    const streams = await this.getAllStreamInfos();
    const streamsMap = new Map();
    for (const stream of streams) {
      streamsMap.set(stream.dbId, stream);
    }

    const changes = [];

    for (const [dbId, offlineTimeout] of timeouts) {
      const stream = streamsMap.get(dbId);

      if (!stream) {
        throw new Error(`Failed to find stream: ${dbId}}`);
      }

      if (typeof offlineTimeout !== 'number') {
        throw new Error(`offline timeout must be a numeric value`);
      }

      if (stream.settings.offlineTimeout !== offlineTimeout) {
        changes.push({
          [QC.LmvDbId]: dbId,
          [QC.Settings]: { ...(stream.settings || {}), offlineTimeout }
        });
      }
    }

    // No changes detected
    if (!changes.length) {
      return;
    }

    const defaultModel = this.facility.getDefaultModel();
    await defaultModel.updateElementsWithData(changes, 'Update streams offline timeouts');

    this.facility.eventTarget.dispatchEvent({ type: dte.DT_STREAMS_INFO_CHANGED_EVENT, dbIds: changes.map((c) => c[QC.LmvDbId]) });
  }

  /**
   * Suggests offline timeouts for streams that received data in the given range.
   *
   * @param {DateRange} range interval of date to be considered
   * @param {int[]} thresholds sorted array of time spans in seconds
   * @returns {Object.<string, int>} dictionary of stream id to threshold
   */
  async suggestOfflineTimeouts(range, thresholds = [
  5 * 60, //  5 minutes
  10 * 60, // 10
  15 * 60, // 15
  60 * 60, // 60
  12 * 60 * 60, // 12 hours
  24 * 60 * 60 // 24 hours
  ]) {
    const result = {};

    const readings = await this.readingsByDate.getReadings(range, GRANULARITY.Raw);
    const byStream = Object.groupBy(readings, (r) => r.k);
    for (let streamId in byStream) {
      let maxGap = 0;
      const bySubstream = Object.groupBy(byStream[streamId], (s) => s.s);
      for (let substreamId in bySubstream) {
        const times = bySubstream[substreamId].flatMap((s) => s.t);
        for (let i = 1; i < times.length; ++i) {
          let d = times[i] - times[i - 1];
          if (d > maxGap) {
            maxGap = d;
          }
        }
      }
      if (maxGap > 0) {
        result[streamId] = thresholds.find((t) => t >= maxGap) ?? 0;
      }
    }
    return result;
  }
}